// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import { useState, useEffect, useRef } from 'react';

// Material UI
import {
  Box,
  Typography,
} from "@mui/material";

// Custom Components
import AligningBox from '../utils/AligningBox';
import { RenderIf } from '../utils/RenderIf';

// Custom Scripts
import { useScreenIsShort } from '../../scripts/utils';



// ----------------------------------------------------------------------------
//   Helper Component: Video Feed Guidelines
// ----------------------------------------------------------------------------

/**
 * Render four corner guides to help visually center oneself in the camera view.
 */
function VideoCanvasGuides({ height, width, inset, length, color, thickness }) {

  // Create reference for canvas element
  const canvasRef = useRef(null);

  // Set the initial canvas size based on the container and the default ratio
  useEffect(() => {

    // Get the canvas target and ensure it exists
    const $canvas = canvasRef.current;
    if (!$canvas) return;

    // Get the context and reset it
    const ctx = $canvas.getContext("2d");
    ctx.reset();

    // Stroke settings
    ctx.lineWidth   = thickness;
    ctx.strokeStyle = color;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";

    // Compute a consistent line length using the smaller dimension
    const lineLength = Math.min( width, height ) * length

    // Top left corner
    ctx.moveTo( width * inset + (lineLength),       height * inset );
    ctx.lineTo( width * inset,                      height * inset );
    ctx.lineTo( width * inset,                      height * inset + (lineLength) );

    // Top right corner
    ctx.moveTo( width * (1 - inset) - (lineLength), height * inset );
    ctx.lineTo( width * (1 - inset),                height * inset );
    ctx.lineTo( width * (1 - inset),                height * inset + (lineLength) );

    // Bottom right corner
    ctx.moveTo( width * (1 - inset) - (lineLength), height * (1 - inset) );
    ctx.lineTo( width * (1 - inset),                height * (1 - inset) );
    ctx.lineTo( width * (1 - inset),                height * (1 - inset) - (lineLength) );

    // Bottom left corner
    ctx.moveTo( width * inset + (lineLength),       height * (1 - inset) );
    ctx.lineTo( width * inset,                      height * (1 - inset) );
    ctx.lineTo( width * inset,                      height * (1 - inset) - (lineLength) );

    // Stroke the whole path
    ctx.stroke();

  }, [ width, height ])

  // Render as a canvas, using objectFit
  return (
    <canvas ref={ canvasRef }
      width  = { width }
      height = { height }
      style={{ "objectFit": "contain", width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
    />
  )
}



// ----------------------------------------------------------------------------
//   Main Component: Video Canvas
// ----------------------------------------------------------------------------

export default function VideoCanvas({
  videoRef,
  defaultRatio = 4 / 3,
  flipped      = false,
  showGuides   = false,
}) {

  // Create reference for the container element
  const containerRef = useRef(null);

  // Create reference for canvas element
  const canvasRef = useRef(null);

  // Track the canvas size
  const [ canvasDimensions, setCanvasDimensions ] = useState({ width: 0, height: 0 });

  // Compute the vertical overhang between the video element and its container
  const verticalOverhang = ( containerRef.current && canvasRef.current?.height )
    ? Math.max(0, containerRef.current.getBoundingClientRect().height - canvasRef.current.height)
    : 0
  ;

  // Check the height of the screen, for responsive layout
  const screenIsShort = useScreenIsShort();


  // --------------------------------------------------------------------------
  //   Effects
  // --------------------------------------------------------------------------

  // Set the initial canvas size based on the container and the default ratio
  useEffect(() => {
    const $canvas  = canvasRef.current;
    const width    = $canvas.parentElement.getBoundingClientRect().width;
    setCanvasDimensions({ width, height: width / defaultRatio })
  }, [])

  // Keep canvas ref dimensions aligned with the state values
  useEffect(() => {
    if (!canvasRef.current) return;
    canvasRef.current.width  = canvasDimensions.width;
    canvasRef.current.height = canvasDimensions.height;
  }, [ canvasDimensions.width, canvasDimensions.height ])

  // Track the video ref
  useEffect(() => {
    if (videoRef.current) startVideo();
  }, [videoRef.current])


  // --------------------------------------------------------------------------
  //   Helper Functions
  // --------------------------------------------------------------------------

  async function startVideo() {

    // The video element to display from
    const $webcam = videoRef.current;

    // The canvas element to display on
    const $canvas = canvasRef.current;
    const ctx = $canvas.getContext('2d');

    /**
     * Callback that begins rendering video content to the canvas element.
     * Sets up an animation loop.
     */
    function renderVideo(event) {

      // Use the video aspect ratio to compute the canvas height
      const aspectRatio = $webcam.videoWidth / $webcam.videoHeight;

      // Fit the canvas into its parent element
      let width  = $canvas.parentElement.getBoundingClientRect().width;
      let height = width / aspectRatio;

      // Set the canvas dimensions to the computed values
      setCanvasDimensions({ width, height })

      // Update width and height on window resize to keep canvas size correct
      window.addEventListener('resize', () => {

        // Fit the canvas into its parent element
        width = $canvas.parentElement.getBoundingClientRect().width;
        height = width / aspectRatio;

        // Set the canvas dimensions to the computed values
        setCanvasDimensions({ width, height })
      })

      // Set up the animation loop
      const draw = () => {
        ctx.drawImage($webcam, 0, 0, width, height);
        requestAnimationFrame(draw);
      }
      requestAnimationFrame(draw);
    }

    // If video element has data, start rendering
    if ($webcam.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      renderVideo(null)
    }

    // If not, wait until the data is available, then start rendering
    else {
      $webcam.addEventListener('canplay', renderVideo, { once: true });
    }
  }


  // --------------------------------------------------------------------------
  //   Component Layout
  // --------------------------------------------------------------------------

  return (
    <Box ref={ containerRef } width="100%" height="100%" backgroundColor="black">
      <canvas ref={ canvasRef } style={{"objectFit": "contain", "width": "100%", "height": "100%", "transform": flipped ? "scaleX(-1)" : ""}}></canvas>

      {/* Optional Guidelines */}
      <RenderIf condition={ showGuides && videoRef.current?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA }>

        {/* Help text - positioned relative to bottom of webcam canvas element */}
        <AligningBox top={0} left={0} alignY="bottom" className="noselect">
          <Box
            marginBottom = { `${ (verticalOverhang / 2) + (canvasRef.current?.height * 0.08) }px` }
            paddingX = {2}
            paddingY = {1}
            backgroundColor = "rgba(0, 0, 0, 0.5)"
            borderRadius = "4px"
          >
            <Typography
              variant    = "h5"
              fontWeight = "bold"
              color      = "rgba(255, 255, 255, 0.8)"
              fontSize   = { screenIsShort ? "1.35em" : '1.5em' }
            >
              Make sure you're centered in the frame!
            </Typography>
          </Box>
        </AligningBox>

        {/* Guidelines object */}
        <VideoCanvasGuides
          height = { canvasRef.current?.height }
          width  = { canvasRef.current?.width  }
          color  = "rgba(255, 255, 255, 0.6)"
          inset  = { 0.08 }
          length = { 0.16 }
          thickness = { 6 }
        />
      </RenderIf>
    </Box>
  )
}
